import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, testid }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
    <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
    <div data-testid={testid} className="text-3xl font-extrabold text-amber-400 mt-1">{value ?? '—'}</div>
  </div>
);

const Tab = ({ id, active, onClick, children, testid }) => (
  <button
    onClick={() => onClick(id)}
    data-testid={testid}
    className={`px-4 py-2 text-sm font-semibold rounded-md transition ${
      active === id ? 'bg-amber-400 text-black' : 'bg-zinc-900 text-zinc-300 hover:text-white'
    }`}
  >
    {children}
  </button>
);

const AdminDashboard = ({ user }) => {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [deals, setDeals] = useState([]);
  const [subs, setSubs] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <h1 className="text-2xl font-bold">Admin access required</h1>
        <p className="text-zinc-400 mt-2">Your account isn't marked as admin.</p>
      </div>
    );
  }

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, d, su, q] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeaders()),
        axios.get(`${API}/admin/deals?limit=100${filterStatus ? `&status=${filterStatus}` : ''}`, authHeaders()),
        axios.get(`${API}/admin/subscribers`, authHeaders()),
        axios.get(`${API}/admin/email-queue`, authHeaders()),
      ]);
      setStats(s.data);
      setDeals(d.data.deals || []);
      setSubs(su.data.subscribers || []);
      setQueue(q.data.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [filterStatus]);

  const runIngest = async () => {
    setBusy(true); setMessage('');
    try {
      const r = await axios.post(`${API}/admin/ingest?generate_ai=true`, {}, authHeaders());
      setMessage(`Ingested ${r.data.new} new deals (${r.data.seen} seen). Errors: ${r.data.errors.length}`);
      loadAll();
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Ingest failed');
    } finally { setBusy(false); }
  };

  const runCampaign = async () => {
    setBusy(true); setMessage('');
    try {
      const r = await axios.post(`${API}/admin/campaign/run?frequency=daily`, {}, authHeaders());
      setMessage(`Campaign queued: ${r.data.queued}, skipped: ${r.data.skipped}`);
      loadAll();
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Campaign failed');
    } finally { setBusy(false); }
  };

  const drainQueue = async () => {
    setBusy(true); setMessage('');
    try {
      const r = await axios.post(`${API}/admin/email-queue/drain?limit=50`, {}, authHeaders());
      const note = r.data.provider ? `via ${r.data.provider}` : (r.data.note || 'no provider');
      setMessage(`Drained queue: sent ${r.data.sent}, failed ${r.data.failed} (${note})`);
      loadAll();
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Drain failed');
    } finally { setBusy(false); }
  };

  const setDealFlag = async (id, patch) => {
    await axios.patch(`${API}/admin/deals/${id}`, patch, authHeaders());
    loadAll();
  };

  const deleteDeal = async (id) => {
    if (!window.confirm('Delete this deal?')) return;
    await axios.delete(`${API}/admin/deals/${id}`, authHeaders());
    loadAll();
  };

  const regenAI = async (id) => {
    await axios.post(`${API}/admin/deals/${id}/ai-summary`, {}, authHeaders());
    loadAll();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 data-testid="admin-title" className="text-3xl font-extrabold">Admin</h1>
        <div className="flex gap-2">
          <button
            onClick={runIngest}
            disabled={busy}
            data-testid="admin-run-ingest"
            className="px-4 py-2 bg-zinc-900 border border-zinc-700 hover:border-amber-400 text-sm rounded-md"
          >
            {busy ? '…' : 'Pull fresh deals'}
          </button>
          <button
            onClick={runCampaign}
            disabled={busy}
            data-testid="admin-run-campaign"
            className="px-4 py-2 bg-amber-400 hover:bg-amber-300 text-black font-semibold text-sm rounded-md"
          >
            Queue daily digest
          </button>
          <button
            onClick={drainQueue}
            disabled={busy}
            data-testid="admin-drain-queue"
            className="px-4 py-2 bg-zinc-900 border border-zinc-700 hover:border-amber-400 text-sm rounded-md"
          >
            Drain queue
          </button>
        </div>
      </div>

      {message && (
        <div data-testid="admin-message" className="mb-4 p-3 rounded-md bg-zinc-900 border border-amber-400/40 text-amber-200 text-sm">
          {message}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <StatCard label="Total deals" value={stats?.total_deals} testid="stat-total" />
        <StatCard label="Active" value={stats?.active_deals} testid="stat-active" />
        <StatCard label="Featured" value={stats?.featured_deals} testid="stat-featured" />
        <StatCard label="Subscribers" value={stats?.subscribers} testid="stat-subs" />
        <StatCard label="Emails queued" value={stats?.email_queue_pending} testid="stat-q-pending" />
        <StatCard label="Emails sent" value={stats?.email_queue_sent} testid="stat-q-sent" />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Tab id="overview" active={tab} onClick={setTab} testid="tab-overview">Top deals</Tab>
        <Tab id="deals" active={tab} onClick={setTab} testid="tab-deals">Manage deals</Tab>
        <Tab id="subs" active={tab} onClick={setTab} testid="tab-subs">Subscribers</Tab>
        <Tab id="queue" active={tab} onClick={setTab} testid="tab-queue">Email queue</Tab>
      </div>

      {loading ? <div className="text-zinc-400">Loading…</div> : (
        <>
          {tab === 'overview' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-950 text-zinc-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-3">Title</th>
                    <th className="text-left p-3">Store</th>
                    <th className="text-right p-3">Sale</th>
                    <th className="text-right p-3">Off</th>
                    <th className="text-right p-3">Clicks</th>
                    <th className="text-right p-3">Sends</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.top_deals || []).map(d => (
                    <tr key={d.id} className="border-t border-zinc-800">
                      <td className="p-3 text-white truncate max-w-[420px]">{d.title}</td>
                      <td className="p-3 text-zinc-300 capitalize">{d.store}</td>
                      <td className="p-3 text-right text-white">{d.sale_price ? `$${d.sale_price.toFixed(2)}` : '-'}</td>
                      <td className="p-3 text-right text-amber-400">{d.discount_percentage || 0}%</td>
                      <td className="p-3 text-right text-zinc-200">{d.clicks}</td>
                      <td className="p-3 text-right text-zinc-200">{d.email_sends}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'deals' && (
            <div>
              <div className="mb-3 flex gap-2">
                <select
                  value={filterStatus}
                  onChange={e => setFilterStatus(e.target.value)}
                  data-testid="admin-filter-status"
                  className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 rounded-md"
                >
                  <option value="">All</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="featured">Featured</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-950 text-zinc-400 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left p-3">Deal</th>
                      <th className="text-left p-3">Store</th>
                      <th className="text-left p-3">Cat</th>
                      <th className="text-right p-3">Sale</th>
                      <th className="text-right p-3">Off</th>
                      <th className="text-center p-3">Flags</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deals.map(d => (
                      <tr key={d.id} className="border-t border-zinc-800">
                        <td className="p-3 max-w-[320px]">
                          <div className="text-white truncate">{d.title}</div>
                          <div className="text-xs text-zinc-500 truncate">{d.source}</div>
                        </td>
                        <td className="p-3 text-zinc-300 capitalize">{d.store}</td>
                        <td className="p-3 text-zinc-300 capitalize">{d.category}</td>
                        <td className="p-3 text-right">{d.sale_price ? `$${d.sale_price.toFixed(2)}` : '-'}</td>
                        <td className="p-3 text-right text-amber-400">{d.discount_percentage || 0}%</td>
                        <td className="p-3 text-center text-xs">
                          {d.is_approved ? <span className="text-emerald-400">✓</span> : <span className="text-red-400">pending</span>}
                          {d.is_featured && <span className="ml-1 text-amber-300">★</span>}
                          {d.is_expired && <span className="ml-1 text-zinc-500">expired</span>}
                        </td>
                        <td className="p-3 text-right space-x-1">
                          <button onClick={() => setDealFlag(d.id, { is_featured: !d.is_featured })} className="px-2 py-1 rounded text-xs border border-zinc-700 hover:border-amber-400">{d.is_featured ? 'Unfeature' : 'Feature'}</button>
                          <button onClick={() => setDealFlag(d.id, { is_approved: !d.is_approved })} className="px-2 py-1 rounded text-xs border border-zinc-700 hover:border-amber-400">{d.is_approved ? 'Reject' : 'Approve'}</button>
                          <button onClick={() => regenAI(d.id)} className="px-2 py-1 rounded text-xs border border-zinc-700 hover:border-amber-400">AI</button>
                          <button onClick={() => deleteDeal(d.id)} className="px-2 py-1 rounded text-xs border border-red-900 text-red-400 hover:border-red-500">×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'subs' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-950 text-zinc-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Frequency</th>
                    <th className="text-left p-3">Cats</th>
                    <th className="text-left p-3">Stores</th>
                    <th className="text-right p-3">Min %</th>
                    <th className="text-left p-3">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map(s => (
                    <tr key={s.id} className="border-t border-zinc-800">
                      <td className="p-3 text-white">{s.email}</td>
                      <td className="p-3 text-zinc-300">{s.frequency}</td>
                      <td className="p-3 text-zinc-400 text-xs">{(s.categories || []).join(', ') || '—'}</td>
                      <td className="p-3 text-zinc-400 text-xs">{(s.stores || []).join(', ') || '—'}</td>
                      <td className="p-3 text-right text-amber-400">{s.min_discount}%</td>
                      <td className="p-3 text-zinc-500 text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'queue' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-950 text-zinc-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-3">To</th>
                    <th className="text-left p-3">Subject</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Deals</th>
                    <th className="text-left p-3">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.map(q => (
                    <tr key={q.id} className="border-t border-zinc-800">
                      <td className="p-3 text-white">{q.to_email}</td>
                      <td className="p-3 text-zinc-300 truncate max-w-[320px]">{q.subject}</td>
                      <td className="p-3"><span className={q.status === 'sent' ? 'text-emerald-400' : q.status === 'failed' ? 'text-red-400' : 'text-zinc-400'}>{q.status}</span></td>
                      <td className="p-3 text-right text-zinc-300">{q.deal_ids.length}</td>
                      <td className="p-3 text-zinc-500 text-xs">{new Date(q.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-4 text-xs text-zinc-500 border-t border-zinc-800">
                Email provider not configured yet — emails stay in queue with status=pending and will send once SendGrid/Resend is wired.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
